import { Button, DialogActions, DialogContent } from "@mui/material";
import type { FormEvent, ReactNode } from "react";
import { useState } from "react";
import { Dialog } from "~components/shared/Dialog/Dialog.tsx";
import { useTranslation } from "~contexts/I18nContext/I18nContext";
import { handleError } from "~lib/handleError";

type ConfirmDialogData = {
  onConfirm: () => void | Promise<void>;
  confirmText: string;
  confirmIcon: ReactNode;
  title: string;
  message: string;
};

type ConfirmDialogProps = {
  data: ConfirmDialogData | null;
  onClose: () => void;
  isModalOpen: boolean;
};

export function ConfirmDialog({
  data,
  onClose,
  isModalOpen,
}: ConfirmDialogProps) {
  const t = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await data?.onConfirm();
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Dialog
      dialogTitle={data?.title}
      open={isModalOpen}
      onClose={handleClose}
      isCancelable={!isSubmitting}
    >
      <form onSubmit={onSubmit}>
        <DialogContent>
          <p>{data?.message}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} type="button" disabled={isSubmitting}>
            {t.cancel}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={data?.confirmIcon}
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {data?.confirmText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
